import React, { useContext, useEffect, useReducer } from 'react';
import axios from 'axios';

// Types for Stripe products
export interface StripePrice {
  id: string;
  object: 'price';
  active: boolean;
  currency: string;
  unit_amount: number;
  unit_amount_decimal: string;
  recurring?: {
    interval: string;
    interval_count: number;
  };
  type: 'one_time' | 'recurring';
  created: number;
  updated: number;
}

export interface StripeProduct {
  id: string;
  object: 'product';
  active: boolean;
  name: string;
  description?: string;
  images: string[];
  metadata: Record<string, string>;
  default_price?: StripePrice;
  prices: StripePrice[];
  created: number;
  updated: number;
}

// Action types
const GET_STRIPE_PRODUCTS_BEGIN = 'GET_STRIPE_PRODUCTS_BEGIN';
const GET_STRIPE_PRODUCTS_SUCCESS = 'GET_STRIPE_PRODUCTS_SUCCESS';
const GET_STRIPE_PRODUCTS_ERROR = 'GET_STRIPE_PRODUCTS_ERROR';
const GET_SINGLE_STRIPE_PRODUCT_BEGIN = 'GET_SINGLE_STRIPE_PRODUCT_BEGIN';
const GET_SINGLE_STRIPE_PRODUCT_SUCCESS = 'GET_SINGLE_STRIPE_PRODUCT_SUCCESS';
const GET_SINGLE_STRIPE_PRODUCT_ERROR = 'GET_SINGLE_STRIPE_PRODUCT_ERROR';

// State interface
interface StripeProductsState {
  stripeProducts: StripeProduct[];
  singleStripeProduct: StripeProduct | null;
  stripeProductsLoading: boolean;
  stripeProductsError: boolean;
  singleStripeProductLoading: boolean;
  singleStripeProductError: boolean;
  fetchStripeProducts: () => void;
  fetchSingleStripeProduct: (productId: string) => void;
}

// Initial state
const initialState: StripeProductsState = {
  stripeProducts: [],
  singleStripeProduct: null,
  stripeProductsLoading: false,
  stripeProductsError: false,
  singleStripeProductLoading: false,
  singleStripeProductError: false,
  fetchStripeProducts: () => {},
  fetchSingleStripeProduct: () => {},
};

// Reducer
const stripeProductsReducer = (state: StripeProductsState, action: any): StripeProductsState => {
  switch (action.type) {
    case GET_STRIPE_PRODUCTS_BEGIN:
      return { ...state, stripeProductsLoading: true };
    case GET_STRIPE_PRODUCTS_SUCCESS:
      return {
        ...state,
        stripeProductsLoading: false,
        stripeProducts: action.payload,
      };
    case GET_STRIPE_PRODUCTS_ERROR:
      return {
        ...state,
        stripeProductsLoading: false,
        stripeProductsError: true,
      };
    case GET_SINGLE_STRIPE_PRODUCT_BEGIN:
      return { ...state, singleStripeProductLoading: true };
    case GET_SINGLE_STRIPE_PRODUCT_SUCCESS:
      return {
        ...state,
        singleStripeProductLoading: false,
        singleStripeProduct: action.payload,
      };
    case GET_SINGLE_STRIPE_PRODUCT_ERROR:
      return {
        ...state,
        singleStripeProductLoading: false,
        singleStripeProductError: true,
      };
    default:
      return state;
  }
};

// Context
const StripeProductsContext = React.createContext<StripeProductsState>(initialState);

// Provider component
export const StripeProductsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(stripeProductsReducer, initialState);

  const fetchStripeProducts = async () => {
    dispatch({ type: GET_STRIPE_PRODUCTS_BEGIN });
    try {
      const response = await axios.get('http://localhost:5050/api/stripe/products');
      dispatch({ type: GET_STRIPE_PRODUCTS_SUCCESS, payload: response.data });
    } catch (error) {
      console.error('Error fetching Stripe products:', error);
      dispatch({ type: GET_STRIPE_PRODUCTS_ERROR });
    }
  };

  const fetchSingleStripeProduct = async (productId: string) => {
    dispatch({ type: GET_SINGLE_STRIPE_PRODUCT_BEGIN });
    try {
      const response = await axios.get(`http://localhost:5050/api/stripe/products/${productId}`);
      dispatch({ type: GET_SINGLE_STRIPE_PRODUCT_SUCCESS, payload: response.data });
    } catch (error) {
      console.error('Error fetching single Stripe product:', error);
      dispatch({ type: GET_SINGLE_STRIPE_PRODUCT_ERROR });
    }
  };

  useEffect(() => {
    fetchStripeProducts();
  }, []);

  return (
    <StripeProductsContext.Provider
      value={{
        ...state,
        fetchStripeProducts,
        fetchSingleStripeProduct,
      }}
    >
      {children}
    </StripeProductsContext.Provider>
  );
};

// Custom hook
export const useStripeProducts = () => {
  const context = useContext(StripeProductsContext);
  if (!context) {
    throw new Error('useStripeProducts must be used within a StripeProductsProvider');
  }
  return context;
}; 